import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useJlptLevels } from "../../hooks/useJlptLevels";
import { canSelectJlptLevel, formatJlptLevels, JLPT_LEVELS, toggleJlptLevel } from "../../utils/jlptLevelPreferences";
import styles from "./header.module.scss";

export default function JlptLevelSelector() {
    const { t } = useTranslation("common");
    const { levels, setLevels, syncStatus } = useJlptLevels();
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const panelId = useId();
    const label = formatJlptLevels(levels);

    useEffect(() => {
        if (!isOpen) return undefined;
        const dismiss = (event: MouseEvent | TouchEvent | KeyboardEvent) => {
            if (event instanceof KeyboardEvent) {
                if (event.key !== "Escape") return;
                setIsOpen(false);
                triggerRef.current?.focus();
                return;
            }
            const target = event.target as Node;
            if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) setIsOpen(false);
        };
        document.addEventListener("mousedown", dismiss);
        document.addEventListener("touchstart", dismiss, { passive: true });
        document.addEventListener("keydown", dismiss);
        return () => {
            document.removeEventListener("mousedown", dismiss);
            document.removeEventListener("touchstart", dismiss);
            document.removeEventListener("keydown", dismiss);
        };
    }, [isOpen]);

    return <div className={styles.jlptSelector}>
        <button type="button" ref={triggerRef} className={styles.jlptBadge}
            onClick={() => setIsOpen((open) => !open)} aria-haspopup="dialog" aria-expanded={isOpen}
            aria-controls={isOpen ? panelId : undefined}
            aria-label={t("jlptSelector.open", { levels: label })}>
            <span aria-hidden="true">{label}</span>
        </button>
        {isOpen && <div ref={panelRef} id={panelId} className={styles.jlptPanel} role="dialog" aria-label={t("jlptSelector.title")}>
            <strong>{t("jlptSelector.title")}</strong>
            <p>{t("jlptSelector.hint")}</p>
            <div className={styles.jlptOptions} role="group" aria-label={t("jlptSelector.title")}>
                {JLPT_LEVELS.map((level) => {
                    const selected = levels.includes(level);
                    const selectable = canSelectJlptLevel(levels, level);
                    return <button key={level} type="button" className={`${styles.jlptOption}${selected ? ` ${styles.jlptOptionSelected}` : ""}`}
                        aria-pressed={selected} disabled={!selected && !selectable}
                        title={!selected && !selectable ? t("jlptSelector.incompatible") : undefined}
                        onClick={() => setLevels(toggleJlptLevel(levels, level))}>N{level}</button>;
                })}
            </div>
            <p className={styles.jlptSelection} aria-live="polite">{t("jlptSelector.selected", { levels: label })}{syncStatus === "error" ? ` ${t("jlptSelector.saveError")}` : ""}</p>
        </div>}
    </div>;
}
